import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $insertTableColumnAtSelection, TableCellNode } from "@lexical/table";

import { default as KeyboardArrowDownIcon } from "@mui/icons-material/KeyboardArrowDown";
import PaddingIcon from "@mui/icons-material/Padding";
import { Box, Stack } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { useCallback, useMemo, useRef, useState, type FC } from "react";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import { $isStylableTableCellMui, StylableTableCellMui } from "../../nodes/stylable-table-cell-mui";
import {
  INSERT_STYLEABLE_TABLE_CELL_COMMAND,
  SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND,
} from "../../types/commands";
import { useStyleableColumns } from "../../utils/use-styleable-columns";
import { ToolbarIconButton } from "../toolbar/toolbar-icon-button";
import { BackgroundColourButton } from "./buttons/background-colour-button";
import { DeleteColumnButton } from "./buttons/delete-column-button";
import { InsertColumnButtons } from "./buttons/insert-column-buttons";
import { TableColumnPopoverSlider } from "./table-column-popover-slider";
import { TableColumnPopoverToggle } from "./table-column-popover-toggle";

export const TableColumnPopoverMenu: FC<{
  tableCellNode: StylableTableCellMui | TableCellNode;
  onClick: () => void;
}> = ({ tableCellNode, onClick }) => {
  const [editor] = useLexicalComposerContext();
  const stylableColumns = useStyleableColumns();
  const popover = usePopover<HTMLButtonElement>();
  const popoverInputRef = useRef<HTMLInputElement>(null);

  const [paddingMenuOpen, setPaddingMenuOpen] = useState(false);

  const handleInsertColumn = useCallback(
    (insertAfter: boolean) => {
      if (!stylableColumns.enabled) {
        editor.update(() => {
          $insertTableColumnAtSelection(insertAfter);
          onClick();
        });
      } else {
        editor.dispatchCommand(INSERT_STYLEABLE_TABLE_CELL_COMMAND, { insertAfter });
        onClick();
      }
    },
    [editor, onClick, stylableColumns],
  );

  const styleableTableCellMui = useMemo(() => {
    if (stylableColumns.enabled && $isStylableTableCellMui(tableCellNode)) {
      return tableCellNode;
    }
    return null;
  }, [tableCellNode, stylableColumns]);

  const initialColour = useMemo(
    () => editor.read(() => tableCellNode.getBackgroundColor() ?? ""),
    [editor, tableCellNode],
  );

  return (
    <>
      {/* eslint-disable-next-line react-hooks/refs */}
      <Box display="flex" flexDirection="column" ref={popover.anchorRef}>
        <DeleteColumnButton onClick={onClick} />
        <InsertColumnButtons handleInsertColumn={handleInsertColumn} />
        <BackgroundColourButton
          initialColour={initialColour}
          nodeKey={tableCellNode.getKey()}
          popoverInputRef={popoverInputRef}
          // eslint-disable-next-line react-hooks/refs
          popover={popover}
        />
        {styleableTableCellMui && (
          <Stack direction="row" spacing={1} alignItems="center">
            <ToolbarIconButton
              data-analytics-id="lexical-table-menu-cell-padding-menu"
              disabled={!editor._editable}
              label="Column Padding"
              icon={<PaddingIcon fontSize="small" />}
              data-testid="table-menu-padding"
              aria-label="change-cell-padding"
              onClick={() => {
                setPaddingMenuOpen((prev) => !prev);
              }}
            />
            <KeyboardArrowDownIcon
              data-analytics-id="lexical-table-menu-cell-padding-menu"
              fontSize="small"
              onClick={() => {
                setPaddingMenuOpen((prev) => !prev);
              }}
              sx={{
                transform: paddingMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          </Stack>
        )}
        <Collapse in={paddingMenuOpen}>
          <Stack direction="column" spacing={1} alignItems="start" sx={{ p: 1 }}>
            {styleableTableCellMui && (
              <TableColumnPopoverToggle
                value={styleableTableCellMui.getIsIndividualPadding()}
                onChange={(isChecked) => {
                  if (isChecked) {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                    });
                  } else {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: false,
                    });
                  }
                }}
                name="individual-padding"
                label="Edit individual paddings"
              />
            )}
            {styleableTableCellMui && !styleableTableCellMui?.getIsIndividualPadding() && (
              <TableColumnPopoverSlider
                value={styleableTableCellMui.getCommonPadding()}
                onChange={(value) => {
                  editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                    nodeKey: tableCellNode.getKey(),
                    individualPadding: false,
                    common: value,
                  });
                }}
                sliderProps={{
                  max: 50,
                  step: 1,
                }}
                label="Column Padding"
                name="common-padding"
              />
            )}
            {styleableTableCellMui && styleableTableCellMui.getIsIndividualPadding() && (
              <>
                <TableColumnPopoverSlider
                  value={styleableTableCellMui.getPaddingTop()}
                  onChange={(value) => {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                      top: value,
                    });
                  }}
                  sliderProps={{
                    max: 50,
                    step: 1,
                  }}
                  label="Column Padding Top"
                  name="padding-top"
                />
                <TableColumnPopoverSlider
                  value={styleableTableCellMui.getPaddingBottom()}
                  onChange={(value) => {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                      bottom: value,
                    });
                  }}
                  sliderProps={{
                    max: 50,
                    step: 1,
                  }}
                  label="Column Padding Bottom"
                  name="padding-bottom"
                />
                <TableColumnPopoverSlider
                  value={styleableTableCellMui.getPaddingLeft()}
                  onChange={(value) => {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                      left: value,
                    });
                  }}
                  sliderProps={{
                    max: 50,
                    step: 1,
                  }}
                  label="Column Padding Left"
                  name="padding-left"
                />
                <TableColumnPopoverSlider
                  value={styleableTableCellMui.getPaddingRight()}
                  onChange={(value) => {
                    editor.dispatchCommand(SET_STYLEABLE_TABLE_CELL_PADDING_COMMAND, {
                      nodeKey: tableCellNode.getKey(),
                      individualPadding: true,
                      right: value,
                    });
                  }}
                  sliderProps={{
                    max: 50,
                    step: 1,
                  }}
                  label="Column Padding Right"
                  name="padding-right"
                />
              </>
            )}
          </Stack>
        </Collapse>
      </Box>
    </>
  );
};
